import { useGetFormValidationError } from "@tm/localization"
import { useMemo } from "react"
import { z } from "zod"

export const useCustomPartSchema = () => {
    const getError = useGetFormValidationError()

    const customPartSchema = useMemo(() => {
        return z.object({
            partNumber: z.string().optional(),
            description: z.string().trim().min(1, getError("REQUIRED")),
            regularPrice: z.number({ required_error: getError("REQUIRED") }).gte(0),
            surcharge: z.number({ required_error: getError("REQUIRED") }).gte(0),
            regularPriceWithSurcharge: z.number({ required_error: getError("REQUIRED") }),
            rebate: z.number({ required_error: getError("REQUIRED") }).gte(0),
            offerPrice: z.number({ required_error: getError("REQUIRED") }),
            vatType: z.number().gt(0, getError("REQUIRED")),
            quantity: z.number().gt(0, getError("MIN_AMOUNT_1")),
        })
    }, [getError])

    const ownArticleSchema = useMemo(() => {
        return z.object({
            categoryId: z.number({ required_error: getError("REQUIRED") }).gt(0, getError("REQUIRED")),
            isDefaultForCategory: z.boolean().optional(),
            note: z.string().optional(),
            customArticleListId: z
                .string({ required_error: getError("REQUIRED") })
                .trim()
                .min(1, getError("REQUIRED")),
        })
    }, [getError])

    return useMemo(() => {
        return z
            .object({
                saveAsOwnArticle: z.boolean().default(false),
            })
            .merge(customPartSchema)
            .merge(ownArticleSchema.partial())
            .superRefine((data, ctx) => {
                if (!data.saveAsOwnArticle) {
                    return
                }

                // if the article should also be saved as an own article, verify the secondary fields and make them required
                const requiredOwnArticleSchema = ownArticleSchema.safeParse(data)
                if (!requiredOwnArticleSchema.success) {
                    requiredOwnArticleSchema.error.issues.forEach((issue) => ctx.addIssue(issue))
                }
            })
    }, [customPartSchema, ownArticleSchema])
}
